import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  fetchCashFlowStatement,
  fetchCashFlowStatementTTM,
  fetchIncomeStatement,
  fetchIncomeStatementTTM,
  CashFlowStatement,
  IncomeStatement,
} from '../../services/stockApi';
import {
  fetchFiscalStandardizedCashFlowStatement,
  fetchFiscalStandardizedCashFlowStatementTtm,
  fetchFiscalStandardizedMetricsList,
  fetchFiscalCashFlowAsReportedFilingIds,
  fetchSecFilingUrlForYear,
  fetchSecFilingUrlForReportDate,
  type FiscalCashRow,
} from '../../services/fiscalApi';
import { fetchFiscalFilingPdfBlobUrl } from '../../services/fiscalApi';
import CenteredLoadingSpinner from '../../components/CenteredLoadingSpinner';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import type { SnackbarVariant } from '../../components/Snackbar';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import ResearchNoteModal from './ResearchNoteModal';
import FloatingNewNoteButton from './FloatingNewNoteButton';

interface ResearchCashFlowScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
  selectedPeriod: 'annual' | 'quarter';
  isAdmin?: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

const ResearchCashFlowScreen: React.FC<ResearchCashFlowScreenProps> = ({
  isDarkMode,
  selectedStock,
  selectedPeriod,
  isAdmin,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const [annualData, setAnnualData] = useState<CashFlowStatement[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<CashFlowStatement[]>([]);
  const [ttmData, setTtmData] = useState<CashFlowStatement | null>(null);
  const [annualIncomeData, setAnnualIncomeData] = useState<IncomeStatement[]>([]);
  const [quarterlyIncomeData, setQuarterlyIncomeData] = useState<IncomeStatement[]>([]);
  const [ttmIncomeData, setTtmIncomeData] = useState<IncomeStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'fiscal' | 'fmp'>('fmp');
  const [fiscalAnnualFilingIdByLabel, setFiscalAnnualFilingIdByLabel] = useState<Record<string, string>>({});
  const [fiscalQuarterFilingIdByLabel, setFiscalQuarterFilingIdByLabel] = useState<Record<string, string>>({});

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCategory, setNoteCategory] = useState('Cash Flow');
  const [noteContent, setNoteContent] = useState('');

  const [showFloatingButton, setShowFloatingButton] = useState<boolean>(true);

  useEffect(() => {
    const update = () => setShowFloatingButton(window.innerWidth >= 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const canPersist =
    !!authToken &&
    typeof authUserId === 'number' &&
    Number.isFinite(authUserId) &&
    authUserId > 0;
  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const hasNoteText = normalizeHtmlText(noteContent).length > 0;
  const canAddNote = noteCategory.trim().length > 0 && hasNoteText && noteTitle.trim().length > 0;

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  const openNewNote = () => {
    setNoteCategory('Cash Flow');
    setNoteTitle('');
    setNoteContent('');
    setIsNoteModalOpen(true);
  };

  const makeCashFlowBase = (symbol: string, date: string, period: string): CashFlowStatement => {
    const fiscalYear = date ? String(new Date(date).getUTCFullYear()) : '';
    return {
      date,
      symbol,
      reportedCurrency: 'USD',
      cik: '',
      filingDate: '',
      acceptedDate: '',
      fiscalYear,
      period,
      netIncome: Number.NaN,
      depreciationAndAmortization: Number.NaN,
      deferredIncomeTax: Number.NaN,
      stockBasedCompensation: Number.NaN,
      changeInWorkingCapital: Number.NaN,
      accountsReceivables: Number.NaN,
      inventory: Number.NaN,
      accountsPayables: Number.NaN,
      otherWorkingCapital: Number.NaN,
      otherNonCashItems: Number.NaN,
      netCashProvidedByOperatingActivities: Number.NaN,
      investmentsInPropertyPlantAndEquipment: Number.NaN,
      acquisitionsNet: Number.NaN,
      purchasesOfInvestments: Number.NaN,
      salesMaturitiesOfInvestments: Number.NaN,
      otherInvestingActivities: Number.NaN,
      netCashUsedForInvestingActivities: Number.NaN,
      debtRepayment: Number.NaN,
      commonStockIssued: Number.NaN,
      commonStockRepurchased: Number.NaN,
      dividendsPaid: Number.NaN,
      netDividendsPaid: Number.NaN,
      commonDividendsPaid: Number.NaN,
      otherFinancingActivities: Number.NaN,
      netCashUsedProvidedByFinancingActivities: Number.NaN,
      effectOfForexChangesOnCash: Number.NaN,
      netChangeInCash: Number.NaN,
      cashAtEndOfPeriod: Number.NaN,
      cashAtBeginningOfPeriod: Number.NaN,
      operatingCashFlow: Number.NaN,
      capitalExpenditure: Number.NaN,
      freeCashFlow: Number.NaN,
    };
  };

  const mapFiscalCashRow = (row: FiscalCashRow, symbol: string, period: 'annual' | 'quarter' | 'ttm'): CashFlowStatement => {
    const date = row.date || '';
    const statement = makeCashFlowBase(symbol, date, period === 'annual' ? 'FY' : period === 'quarter' ? 'Q' : 'TTM');
    const operating = row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities;

    if (Number.isFinite(row.netIncome)) {
      statement.netIncome = row.netIncome as number;
    }
    if (Number.isFinite(row.depreciationAndAmortization)) {
      statement.depreciationAndAmortization = row.depreciationAndAmortization as number;
    }
    if (Number.isFinite(row.stockBasedCompensation)) {
      statement.stockBasedCompensation = row.stockBasedCompensation as number;
    }
    if (Number.isFinite(row.changeInWorkingCapital)) {
      statement.changeInWorkingCapital = row.changeInWorkingCapital as number;
    }
    if (Number.isFinite(row.accountsReceivables)) {
      statement.accountsReceivables = row.accountsReceivables as number;
    }
    if (Number.isFinite(row.inventory)) {
      statement.inventory = row.inventory as number;
    }
    if (Number.isFinite(row.accountsPayables)) {
      statement.accountsPayables = row.accountsPayables as number;
    }
    if (Number.isFinite(row.otherWorkingCapital)) {
      statement.otherWorkingCapital = row.otherWorkingCapital as number;
    }
    if (Number.isFinite(row.changesInIncomeTaxes)) {
      statement.changesInIncomeTaxes = row.changesInIncomeTaxes as number;
    }
    if (Number.isFinite(row.changesInDeferredRevenue)) {
      statement.changesInDeferredRevenue = row.changesInDeferredRevenue as number;
    }
    if (Number.isFinite(row.otherNonCashItems)) {
      statement.otherNonCashItems = row.otherNonCashItems as number;
    }
    if (Number.isFinite(row.investmentsInPropertyPlantAndEquipment)) {
      statement.investmentsInPropertyPlantAndEquipment = row.investmentsInPropertyPlantAndEquipment as number;
      statement.capitalExpenditure = row.investmentsInPropertyPlantAndEquipment as number;
    }
    if (Number.isFinite(row.purchasesOfInvestments)) {
      statement.purchasesOfInvestments = row.purchasesOfInvestments as number;
    }
    if (Number.isFinite(row.salesMaturitiesOfInvestments)) {
      statement.salesMaturitiesOfInvestments = row.salesMaturitiesOfInvestments as number;
    }
    if (Number.isFinite(row.acquisitionsNet)) {
      statement.acquisitionsNet = row.acquisitionsNet as number;
    }
    if (Number.isFinite(row.otherInvestingActivities)) {
      statement.otherInvestingActivities = row.otherInvestingActivities as number;
    }
    if (Number.isFinite(row.netCashUsedForInvestingActivities)) {
      statement.netCashUsedForInvestingActivities = row.netCashUsedForInvestingActivities as number;
    }
    if (Number.isFinite(row.debtRepayment)) {
      statement.debtRepayment = row.debtRepayment as number;
    }
    if (Number.isFinite(row.commonStockIssued)) {
      statement.commonStockIssued = row.commonStockIssued as number;
    }
    if (Number.isFinite(row.commonStockRepurchased)) {
      statement.commonStockRepurchased = row.commonStockRepurchased as number;
    }
    if (Number.isFinite(row.commonDividendsPaid)) {
      statement.commonDividendsPaid = row.commonDividendsPaid as number;
      statement.dividendsPaid = row.commonDividendsPaid as number;
      statement.netDividendsPaid = row.commonDividendsPaid as number;
    }
    if (Number.isFinite(row.otherFinancingActivities)) {
      statement.otherFinancingActivities = row.otherFinancingActivities as number;
    }
    if (Number.isFinite(row.netCashUsedProvidedByFinancingActivities)) {
      statement.netCashUsedProvidedByFinancingActivities = row.netCashUsedProvidedByFinancingActivities as number;
    }
    if (Number.isFinite(row.effectOfForexChangesOnCash)) {
      statement.effectOfForexChangesOnCash = row.effectOfForexChangesOnCash as number;
    }
    if (Number.isFinite(row.netChangeInCash)) {
      statement.netChangeInCash = row.netChangeInCash as number;
    }
    if (Number.isFinite(operating)) {
      statement.operatingCashFlow = operating as number;
      statement.netCashProvidedByOperatingActivities = operating as number;
    }
    if (Number.isFinite(statement.operatingCashFlow) && Number.isFinite(statement.capitalExpenditure)) {
      // Capital expenditure is already negative in fiscal data, so add it to operating cash flow.
      statement.freeCashFlow = (statement.operatingCashFlow as number) + (statement.capitalExpenditure as number);
    }

    return statement;
  };

  const buildFiscalCashTtmFromQuarters = (rows: CashFlowStatement[], symbol: string): CashFlowStatement | null => {
    const sortedDesc = [...rows]
      .filter((row) => row?.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const latest4 = sortedDesc.slice(0, 4);
    if (latest4.length < 4) return null;

    const sum4 = (getter: (row: CashFlowStatement) => number | undefined) => {
      const values = latest4.map((row) => getter(row));
      const numeric = values.filter((v): v is number => Number.isFinite(v));
      if (numeric.length !== values.length) return Number.NaN;
      return numeric.reduce((acc, value) => acc + value, 0);
    };

    const ttm = makeCashFlowBase(symbol, latest4[0]?.date ?? '', 'TTM');

    ttm.netIncome = sum4((row) => row.netIncome);
    ttm.depreciationAndAmortization = sum4((row) => row.depreciationAndAmortization);
    ttm.stockBasedCompensation = sum4((row) => row.stockBasedCompensation);
    ttm.changeInWorkingCapital = sum4((row) => row.changeInWorkingCapital);
    ttm.accountsReceivables = sum4((row) => row.accountsReceivables);
    ttm.inventory = sum4((row) => row.inventory);
    ttm.accountsPayables = sum4((row) => row.accountsPayables);
    ttm.otherWorkingCapital = sum4((row) => row.otherWorkingCapital);
    ttm.otherNonCashItems = sum4((row) => row.otherNonCashItems);

    const operating = sum4((row) => row.netCashProvidedByOperatingActivities ?? row.operatingCashFlow);
    ttm.netCashProvidedByOperatingActivities = operating;
    ttm.operatingCashFlow = operating;

    const capex = sum4((row) => row.investmentsInPropertyPlantAndEquipment ?? row.capitalExpenditure);
    ttm.investmentsInPropertyPlantAndEquipment = capex;
    ttm.capitalExpenditure = capex;

    ttm.acquisitionsNet = sum4((row) => row.acquisitionsNet);
    ttm.purchasesOfInvestments = sum4((row) => row.purchasesOfInvestments);
    ttm.salesMaturitiesOfInvestments = sum4((row) => row.salesMaturitiesOfInvestments);
    ttm.otherInvestingActivities = sum4((row) => row.otherInvestingActivities);
    ttm.netCashUsedForInvestingActivities = sum4((row) => row.netCashUsedForInvestingActivities);

    ttm.debtRepayment = sum4((row) => row.debtRepayment);
    ttm.commonStockIssued = sum4((row) => row.commonStockIssued);
    ttm.commonStockRepurchased = sum4((row) => row.commonStockRepurchased);
    ttm.commonDividendsPaid = sum4((row) => row.commonDividendsPaid ?? row.dividendsPaid ?? row.netDividendsPaid);
    ttm.dividendsPaid = ttm.commonDividendsPaid;
    ttm.netDividendsPaid = ttm.commonDividendsPaid;
    ttm.otherFinancingActivities = sum4((row) => row.otherFinancingActivities);
    ttm.netCashUsedProvidedByFinancingActivities = sum4((row) => row.netCashUsedProvidedByFinancingActivities);

    ttm.effectOfForexChangesOnCash = sum4((row) => row.effectOfForexChangesOnCash);
    ttm.netChangeInCash = sum4((row) => row.netChangeInCash);

    if (Number.isFinite(ttm.operatingCashFlow) && Number.isFinite(ttm.capitalExpenditure)) {
      ttm.freeCashFlow = (ttm.operatingCashFlow as number) + (ttm.capitalExpenditure as number);
    }

    return ttm;
  };


  const loadCashFlowData = React.useCallback(async () => {
    setLoading(true);
    try {
      const metrics = await fetchFiscalStandardizedMetricsList();
      const [fiscalStatement, fiscalTtm, incomeAnnual, incomeQuarter, incomeTtm] = await Promise.all([
        fetchFiscalStandardizedCashFlowStatement(selectedStock, metrics),
        fetchFiscalStandardizedCashFlowStatementTtm(selectedStock, metrics),
        fetchIncomeStatement(selectedStock, 'annual', 15).catch(() => []),
        fetchIncomeStatement(selectedStock, 'quarter', 15).catch(() => []),
        fetchIncomeStatementTTM(selectedStock, 1).catch(() => null),
      ]);

      setAnnualIncomeData(incomeAnnual);
      setQuarterlyIncomeData(incomeQuarter);
      setTtmIncomeData(incomeTtm);

      const fiscalCount = fiscalStatement.annual.length + fiscalStatement.quarter.length;
      const errorText = String(fiscalStatement.error || fiscalTtm.error || '').trim();
      const unavailableByMessage =
        Boolean(errorText) && /data is not yet available|companies-list|companies list/i.test(errorText);
      const fiscalUnavailable = unavailableByMessage || fiscalCount === 0;

      if (fiscalUnavailable) {
        const [annual, quarterly, ttm] = await Promise.all([
          fetchCashFlowStatement(selectedStock, 'annual', 15),
          fetchCashFlowStatement(selectedStock, 'quarter', 15),
          fetchCashFlowStatementTTM(selectedStock, 1)
        ]);
        setAnnualData(annual);
        setQuarterlyData(quarterly);
        setTtmData(ttm);
        setDataSource('fmp');
        return;
      }

      const fiscalAnnual = fiscalStatement.annual.map((row) =>
        mapFiscalCashRow(row, selectedStock, 'annual')
      );
      const fiscalQuarter = fiscalStatement.quarter.map((row) =>
        mapFiscalCashRow(row, selectedStock, 'quarter')
      );
      const ttmFromQuarters = buildFiscalCashTtmFromQuarters(fiscalQuarter, selectedStock);
      const ttmFromApi = fiscalTtm.row ? mapFiscalCashRow(fiscalTtm.row, selectedStock, 'ttm') : null;
      const fiscalTtmRow = ttmFromQuarters ?? ttmFromApi;

      setAnnualData(fiscalAnnual);
      setQuarterlyData(fiscalQuarter);
      setTtmData(fiscalTtmRow);
      setDataSource('fiscal');
    } catch (error) {
      console.error('Error loading cash flow statement:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStock]);

  useEffect(() => {
    loadCashFlowData();
  }, [loadCashFlowData]);

  useEffect(() => {
    let cancelled = false;

    const formatLabel = (dateStr: string): string => {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr;
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      return `${month} ${year}`;
    };

    const buildLabelMap = (rows: Array<{ filingId?: string; date?: string }>) => {
      const map: Record<string, string> = {};
      rows.forEach((row) => {
        const id = String(row.filingId ?? '').trim();
        const date = String(row.date ?? '').trim();
        if (!id || !date) return;
        const label = formatLabel(date);
        if (!map[label]) map[label] = id;
      });
      return map;
    };

    (async () => {
      try {
        const result = await fetchFiscalCashFlowAsReportedFilingIds(selectedStock);
        if (cancelled) return;
        setFiscalAnnualFilingIdByLabel(buildLabelMap(result.annual ?? []));
        setFiscalQuarterFilingIdByLabel(buildLabelMap(result.quarter ?? []));
      } catch {
        if (!cancelled) {
          setFiscalAnnualFilingIdByLabel({});
          setFiscalQuarterFilingIdByLabel({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedStock]);

  const openFiscalFilingPdfById = useCallback(
    async (filingId?: string) => {
      const safeId = String(filingId ?? '').trim();
      if (!safeId) {
        onNotify?.('Missing filing ID for this year.', 'error');
        return;
      }

      const blobUrl = await fetchFiscalFilingPdfBlobUrl(selectedStock, safeId);
      if (!blobUrl) {
        onNotify?.('Unable to load filing PDF. Please login and try again.', 'error');
        return;
      }

      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    },
    [onNotify, selectedStock]
  );

  const openAnnualFilingForYear = useCallback(
    async (year: number, fallbackId?: string) => {
      if (!Number.isFinite(year)) {
        onNotify?.('Missing filing year.', 'error');
        return;
      }

      const secUrl = await fetchSecFilingUrlForYear(selectedStock, year, '10-K');
      if (secUrl) {
        window.open(secUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (fallbackId) {
        await openFiscalFilingPdfById(fallbackId);
        return;
      }

      onNotify?.('Unable to load annual filing for this year.', 'error');
    },
    [onNotify, openFiscalFilingPdfById, selectedStock]
  );

  const openQuarterFilingForDate = useCallback(
    async (reportDate: string, fallbackId?: string, form: string = '10-Q') => {
      const rawDate = String(reportDate ?? '').trim();
      if (!rawDate) {
        onNotify?.('Missing filing date.', 'error');
        return;
      }

      const normalizedDate = (() => {
        const match = rawDate.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) return match[0];
        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) return rawDate;
        return parsed.toISOString().slice(0, 10);
      })();

      const secUrl = await fetchSecFilingUrlForReportDate(selectedStock, normalizedDate, form);
      if (secUrl) {
        window.open(secUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (fallbackId) {
        await openFiscalFilingPdfById(fallbackId);
        return;
      }

      onNotify?.('Unable to load quarterly filing for this date.', 'error');
    },
    [onNotify, openFiscalFilingPdfById, selectedStock]
  );

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';

    if (Math.abs(value) >= 1e9) {
      return (value / 1e9).toFixed(2) + 'B';
    } else if (Math.abs(value) >= 1e6) {
      return (value / 1e6).toFixed(2) + 'M';
    }
    return value.toFixed(2);
  };

  const getSharesFromIncome = (row: IncomeStatement | null | undefined): number | undefined => {
    if (!row) return undefined;
    const diluted = row.weightedAverageShsOutDil;
    if (Number.isFinite(diluted) && diluted > 0) return diluted;
    const basic = row.weightedAverageShsOut;
    if (Number.isFinite(basic) && basic > 0) return basic;
    return undefined;
  };

  const getTtmFallbackShares = (): number | undefined => {
    const latest4 = [...quarterlyIncomeData]
      .filter((row) => row?.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 4);
    if (latest4.length < 4) return undefined;
    const values = latest4
      .map((row) => getSharesFromIncome(row))
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
    if (values.length !== 4) return undefined;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  const getSharesForCashRow = (cf: CashFlowStatement): number | undefined => {
    const period = String(cf.period || '').toUpperCase();
    if (period === 'TTM') {
      return getSharesFromIncome(ttmIncomeData) ?? getTtmFallbackShares();
    }

    const candidates = period === 'Q' ? quarterlyIncomeData : annualIncomeData;
    if (!Array.isArray(candidates) || candidates.length === 0) return undefined;

    const exact = candidates.find((row) => row.date === cf.date);
    const exactShares = getSharesFromIncome(exact);
    if (exactShares) return exactShares;

    const fiscalYear = String(cf.fiscalYear || '').trim();
    if (fiscalYear) {
      const byYear = candidates.find((row) => String(row.fiscalYear || '').trim() === fiscalYear);
      const byYearShares = getSharesFromIncome(byYear);
      if (byYearShares) return byYearShares;
    }

    return undefined;
  };

  const formatPerShare = (rawValue: number | null | undefined, cf: CashFlowStatement): string => {
    if (rawValue == null || !Number.isFinite(rawValue)) return 'N/A';
    const shares = getSharesForCashRow(cf);
    if (!shares || !Number.isFinite(shares) || shares <= 0) return 'N/A';
    return formatNumber(rawValue / shares);
  };
  
  const withNegativeColor = (base: React.CSSProperties, renderedText: string): React.CSSProperties => {
    if (typeof renderedText !== 'string') return base;
    const trimmed = renderedText.trim();
    if (trimmed.startsWith('-')) return { ...base, color: '#F44336' };
    return base;
  };

  const formatDate = (dateStr: string, period: 'annual' | 'quarter'): string => {
    const date = new Date(dateStr);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);

    // Use the actual month from the statement date (matches Income Statement behavior).
    // This correctly shows fiscal-year ends like Jun for MSFT.
    return `${month} ${year}`;
  };

  const annualSortedDesc = [...annualData]
    .filter((d) => d?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const quarterlySortedDesc = [...quarterlyData]
    .filter((d) => d?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const annualPeriods = annualSortedDesc.slice(0, 10).reverse();
  const quarterlyPeriods = quarterlySortedDesc.slice(0, 5).reverse();

  const annualDateSet = useMemo(() => {
    const set = new Set<string>();
    annualPeriods.forEach((row) => {
      if (row?.date) set.add(row.date);
    });
    return set;
  }, [annualPeriods]);

  const latestAnnualYear = useMemo(() => {
    if (annualPeriods.length === 0) return null;
    const last = annualPeriods[annualPeriods.length - 1]?.date;
    if (!last) return null;
    const y = new Date(last).getFullYear();
    return Number.isFinite(y) ? y : null;
  }, [annualPeriods]);

  const latestAnnualDateKey = useMemo(() => {
    if (annualPeriods.length === 0) return null;
    return annualPeriods[annualPeriods.length - 1]?.date ?? null;
  }, [annualPeriods]);

  const latestQuarterDateForAnnualYear = useMemo(() => {
    if (latestAnnualYear == null) return null;
    const matches = quarterlyPeriods.filter((q) => {
      const y = new Date(q.date).getFullYear();
      return Number.isFinite(y) && y === latestAnnualYear;
    });
    if (matches.length === 0) return null;
    return matches[matches.length - 1]?.date ?? null;
  }, [latestAnnualYear, quarterlyPeriods]);

  const getHighlightedQuarterDate = useCallback(
    (quarters: Array<{ date: string }>) => {
      if (latestAnnualDateKey) {
        const match = quarters.find((q) => q.date === latestAnnualDateKey);
        if (match) return latestAnnualDateKey;
      }
      return latestQuarterDateForAnnualYear;
    },
    [latestAnnualDateKey, latestQuarterDateForAnnualYear]
  );

  const highlightedQuarterDate = useMemo(
    () => getHighlightedQuarterDate(quarterlyPeriods),
    [getHighlightedQuarterDate, quarterlyPeriods]
  );

  type Row = {
    label: string;
    getValue: (cf: CashFlowStatement) => string;
    isBold?: boolean;
    isSpacer?: boolean;
  };

  const workingCapitalLabel = dataSource === 'fiscal' ? 'Other Adjustments' : 'Change in Working Capital';
  const otherWorkingCapitalLabel =
    dataSource === 'fiscal' ? 'Changes in Accrued Expenses' : 'Changes in Other Working Capital';

  const rows: Row[] = [
    { label: 'Net Income', getValue: (cf: CashFlowStatement) => formatNumber(cf.netIncome) },
    { label: 'Depreciation & Amortization', getValue: (cf: CashFlowStatement) => formatNumber(cf.depreciationAndAmortization) },
    ...(dataSource === 'fmp'
      ? [{ label: 'Deferred Income Tax', getValue: (cf: CashFlowStatement) => formatNumber(cf.deferredIncomeTax) }]
      : []),
    { label: 'Stock Based Compensation', getValue: (cf: CashFlowStatement) => formatNumber(cf.stockBasedCompensation) },
    { label: workingCapitalLabel, getValue: (cf: CashFlowStatement) => formatNumber(cf.changeInWorkingCapital) },
    { label: 'Changes in Accounts Receivable', getValue: (cf: CashFlowStatement) => formatNumber(cf.accountsReceivables) },
    { label: 'Changes in Inventory', getValue: (cf: CashFlowStatement) => formatNumber(cf.inventory) },
    { label: 'Changes in Accounts Payable', getValue: (cf: CashFlowStatement) => formatNumber(cf.accountsPayables) },
    { label: otherWorkingCapitalLabel, getValue: (cf: CashFlowStatement) => formatNumber(cf.otherWorkingCapital) },
    { label: 'Other Non-Cash Items', getValue: (cf: CashFlowStatement) => formatNumber(cf.otherNonCashItems) },
    {
      label: 'Cash Flow From Operating Activities',
      getValue: (cf: CashFlowStatement) => formatNumber(cf.netCashProvidedByOperatingActivities),
      isBold: true,
    },
    { label: '', getValue: () => '', isSpacer: true },
    { label: 'Property Plant and Equipment', getValue: (cf: CashFlowStatement) => formatNumber(cf.investmentsInPropertyPlantAndEquipment) },
    { label: 'Acquisitions', getValue: (cf: CashFlowStatement) => formatNumber(cf.acquisitionsNet) },
    { label: 'Purchases of Investments', getValue: (cf: CashFlowStatement) => formatNumber(cf.purchasesOfInvestments) },
    { label: 'Sales and Maturities of Investments', getValue: (cf: CashFlowStatement) => formatNumber(cf.salesMaturitiesOfInvestments) },
    { label: 'Other Investing Activities', getValue: (cf: CashFlowStatement) => formatNumber(cf.otherInvestingActivities) },
    {
      label: 'Cash Flow From Investing',
      getValue: (cf: CashFlowStatement) =>
        formatNumber(((cf as any).netCashProvidedByInvestingActivities ?? cf.netCashUsedForInvestingActivities) as number | undefined),
      isBold: true,
    },
    { label: '', getValue: () => '', isSpacer: true },

    { label: 'Net Debt Issuance (Payment)', getValue: (cf: CashFlowStatement) => formatNumber(((cf as any).netDebtIssuance ?? cf.debtRepayment) as number | undefined) },
    { label: 'Common Stock Issued', getValue: (cf: CashFlowStatement) => formatNumber(((cf as any).commonStockIssuance ?? cf.commonStockIssued) as number | undefined) },
    { label: 'Common Stock Repurchased', getValue: (cf: CashFlowStatement) => formatNumber(cf.commonStockRepurchased) },
    {
      label: 'Dividends Paid',
      getValue: (cf: CashFlowStatement) =>
        formatNumber((cf.commonDividendsPaid ?? cf.dividendsPaid ?? cf.netDividendsPaid) as number | undefined),
    },
    { label: 'Other Financing Activities', getValue: (cf: CashFlowStatement) => formatNumber(cf.otherFinancingActivities) },
    {
      label: 'Cash Flow From Financing',
      getValue: (cf: CashFlowStatement) =>
        formatNumber(((cf as any).netCashProvidedByFinancingActivities ?? cf.netCashUsedProvidedByFinancingActivities) as number | undefined),
      isBold: true,
    },

    { label: '', getValue: () => '', isSpacer: true },

    { label: 'Cash at Beginning of Period', getValue: (cf: CashFlowStatement) => formatNumber(cf.cashAtBeginningOfPeriod) },
    { label: 'Effect of Forex Changes on Cash', getValue: (cf: CashFlowStatement) => formatNumber(cf.effectOfForexChangesOnCash) },
    { label: 'Net Change in Cash', getValue: (cf: CashFlowStatement) => formatNumber(cf.netChangeInCash) },
    { label: 'Cash at End of Period', getValue: (cf: CashFlowStatement) => formatNumber(cf.cashAtEndOfPeriod), isBold: true },

    { label: '', getValue: () => '', isSpacer: true },

    { label: 'Capital Expenditure', getValue: (cf: CashFlowStatement) => formatNumber(cf.capitalExpenditure) },
    { label: 'Free Cash Flow', getValue: (cf: CashFlowStatement) => formatNumber(cf.freeCashFlow) },
    { label: '', getValue: () => '', isSpacer: true },
    {
      label: 'OCF/Share',
      getValue: (cf: CashFlowStatement) =>
        formatPerShare(cf.netCashProvidedByOperatingActivities ?? cf.operatingCashFlow, cf),
    },
    {
      label: 'FCF/Share',
      getValue: (cf: CashFlowStatement) => formatPerShare(cf.freeCashFlow, cf),
    },
  ];

  if (dataSource === 'fiscal') {
    const insertIndex = rows.findIndex((row) => row.label === otherWorkingCapitalLabel);
    if (insertIndex !== -1) {
      rows.splice(insertIndex + 1, 0, {
        label: 'Changes in Income Taxes',
        getValue: (cf: CashFlowStatement) => formatNumber(cf.changesInIncomeTaxes),
      });
      rows.splice(insertIndex + 2, 0, {
        label: 'Changes in Deferred Revenue',
        getValue: (cf: CashFlowStatement) => formatNumber(cf.changesInDeferredRevenue),
      });
    }
  }

  const filteredRows = dataSource === 'fiscal'
    ? rows.filter((row) => row.label !== 'Cash at Beginning of Period' && row.label !== 'Cash at End of Period')
    : rows;

  const containerStyle: React.CSSProperties = {
    // Keep consistent spacing below Header `tabs-bar`.
    padding: '15px 2rem 2rem 2rem',
    minHeight: '100vh',
    backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000'
  };

  const tableContainerStyle: React.CSSProperties = {
    width: '95.3%',
    marginLeft: '1rem',
    padding: '0 0.5rem 0.5rem 0.5rem',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '75vh',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    position: 'relative'
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '1rem',
    textAlign: 'center',
    borderBottom: `2px solid ${isDarkMode ? '#374151' : '#e0e0e0'}`,
    borderRight: `1px solid ${isDarkMode ? '#374151' : '#e0e0e0'}`,
    backgroundColor: isDarkMode ? '#1F2937' : '#f5f5f5',
    fontWeight: '500',
    fontSize: '0.88rem'
  };

  const showSourceLabels = Boolean(isAdmin);
  const sourceLabelText = dataSource === 'fiscal' ? 'fiscal.ai' : 'FMP';
  const floatingSourceLabelStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '0.35rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
    pointerEvents: 'none',
    zIndex: 8,
    padding: '0.1rem 0.35rem',
    background: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '6px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e5e7eb'}`
  };
  
  // Sticky table headers: we have 2 header rows (group labels + actual column labels).
  // Keep both visible by stacking with a fixed top offset.
  const groupHeaderRowTopOffsetPx = 30;
  
  const stickyHeaderCellStyle: React.CSSProperties = {
    ...headerCellStyle,
    position: 'sticky',
    top: groupHeaderRowTopOffsetPx,
    zIndex: 3,
  };
  
  const stickyHeaderFirstCellStyle: React.CSSProperties = {
    ...stickyHeaderCellStyle,
    left: 0,
    zIndex: 4,
  };

  const cellStyle: React.CSSProperties = {
    padding: '1rem',
    textAlign: 'center',
    borderBottom: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderLeft: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
  };

  const firstColumnStyle: React.CSSProperties = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
    position: 'sticky',
    left: 0,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    zIndex: 1,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`
  };

  const sectionHeaderStyle: React.CSSProperties = {
    ...headerCellStyle,
    backgroundColor: isDarkMode ? 'rgba(26, 26, 26, 1)' : '#e8e8e8',
    fontWeight: 700,
    fontSize: '14px'
  };

  const ttmCellStyle: React.CSSProperties = {
    ...cellStyle,
    borderLeft: `3px solid ${isDarkMode ? '#555' : '#999'}`
  };

  const ttmHeaderStyle: React.CSSProperties = {
    ...headerCellStyle,
    borderLeft: `3px solid ${isDarkMode ? '#555' : '#999'}`
  };
  
  const stickyTtmHeaderStyle: React.CSSProperties = {
    ...ttmHeaderStyle,
    position: 'sticky',
    top: groupHeaderRowTopOffsetPx,
    zIndex: 3,
  };
  
  const stickyGroupHeaderCellStyle: React.CSSProperties = {
    background: isDarkMode ? '#111827' : '#fff',
    height: `${groupHeaderRowTopOffsetPx}px`,
    fontSize: '14px',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    fontWeight: 300,
    border: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 5,
  };
  
  const stickyGroupHeaderLeftCellStyle: React.CSSProperties = {
    ...stickyGroupHeaderCellStyle,
    left: 0,
    zIndex: 6,
  };

  const headerLinkStyle: React.CSSProperties = {
    color: isDarkMode ? '#93C5FD' : '#1D4ED8',
    textDecoration: 'underline',
    cursor: 'pointer',
  };

  const headerLinkButtonStyle: React.CSSProperties = {
    ...headerLinkStyle,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  };

  const spacerCellStyle: React.CSSProperties = {
    ...cellStyle,
    padding: '0.25rem',
    borderLeft: 'none',
    borderRight: 'none',
  };

  const spacerFirstColumnStyle: React.CSSProperties = {
    ...firstColumnStyle,
    padding: '0.25rem',
    borderLeft: 'none',
    borderRight: 'none',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerCellStyle}>Cash Flow Statement</div>
        <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight="240px" />

        <FloatingNewNoteButton
          visible={showFloatingButton}
          onClick={openNewNote}
          label="New Note"
          backgroundColor={isDarkMode ? '#374151' : undefined}
        />

        <ResearchNoteModal
          isOpen={isNoteModalOpen}
          onRequestClose={() => setIsNoteModalOpen(false)}
          isDarkMode={isDarkMode}
          noteTitle={noteTitle}
          onChangeTitle={setNoteTitle}
          noteCategory={noteCategory}
          onChangeCategory={setNoteCategory}
          noteContent={noteContent}
          onChangeContent={setNoteContent}
          categories={RESEARCH_DESK_NOTE_CATEGORIES}
          canSubmit={canAddNote}
          onSubmit={async () => {
            if (!canPersist) {
              onNotify?.('Please login to save notes.', 'error');
              return;
            }

            const title = extractTitleFromContent(noteContent);
            if (!title) return;
            const associatedWith = noteCategory.trim();
            if (!associatedWith) return;

            try {
              await adminApi.createResearchDeskNote(authToken as string, {
                userId: authUserId as number,
                title,
                associatedWith,
                stockSymbol: selectedStock,
                html: noteContent ?? '',
              });
              setIsNoteModalOpen(false);
              setNoteTitle('');
              setNoteContent('');
              setNoteCategory('Cash Flow');
              onNotify?.(`${selectedStock} • Cash Flow • Saved`, 'success');
            } catch (e: any) {
              if (isUnauthorized(e)) onUnauthorized?.();
            }
          }}
        />
      </div>
    );
  }

  return (
    <div id="cash-flow-container" style={containerStyle}>
      <div id="cash-flow-table-wrapper" style={tableContainerStyle}>
        {showSourceLabels ? <div style={floatingSourceLabelStyle}>{sourceLabelText}</div> : null}
        <table id="cash-flow-table" style={tableStyle}>
          <thead>
            <tr>
              <th style={stickyGroupHeaderLeftCellStyle}></th>
              <th
                colSpan={annualPeriods.length}
                style={{ ...stickyGroupHeaderCellStyle, textAlign: 'left', padding: '5px' }}
              >
                Annuals
              </th>
              <th style={{ ...stickyGroupHeaderCellStyle, textAlign: 'center', padding: '5px' }}></th>
              <th
                colSpan={quarterlyPeriods.length}
                style={{ ...stickyGroupHeaderCellStyle, textAlign: 'left', padding: '1px' }}
              >
                Quarterly
              </th>
            </tr>
            <tr>
              <th style={stickyHeaderFirstCellStyle}>Metric</th>
              {annualPeriods.map((data, idx) => (
                <th
                  key={`annual-${idx}`}
                  style={{
                    ...stickyHeaderCellStyle,
                    fontWeight: latestAnnualYear != null && new Date(data.date).getFullYear() === latestAnnualYear ? 800 : stickyHeaderCellStyle.fontWeight,
                  }}
                >
                  {(() => {
                    const label = formatDate(data.date, 'annual');
                    const year = new Date(data.date).getFullYear();
                    const directFilingId = fiscalAnnualFilingIdByLabel[label];
                    return (
                      <button type="button" onClick={() => openAnnualFilingForYear(year, directFilingId)} style={headerLinkButtonStyle}>
                        {formatDate(data.date, 'annual')}
                      </button>
                    );
                  })()}
                </th>
              ))}
              <th style={stickyTtmHeaderStyle}>TTM</th>
              {quarterlyPeriods.map((data, idx) => (
                <th
                  key={`quarter-${idx}`}
                  style={{
                    ...stickyHeaderCellStyle,
                    fontWeight: highlightedQuarterDate && data.date === highlightedQuarterDate ? 800 : stickyHeaderCellStyle.fontWeight,
                  }}
                >
                  {(() => {
                    const label = formatDate(data.date, 'quarter');
                    const isAnnualLike = annualDateSet.has(data.date);
                    const directFilingId = isAnnualLike
                      ? fiscalAnnualFilingIdByLabel[label]
                      : fiscalQuarterFilingIdByLabel[label];
                    return (
                      <button
                        type="button"
                        onClick={() => openQuarterFilingForDate(data.date, directFilingId, isAnnualLike ? '10-K' : '10-Q')}
                        style={headerLinkButtonStyle}
                      >
                        {formatDate(data.date, 'quarter')}
                      </button>
                    );
                  })()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.isSpacer ? (
                  <>
                    <td style={spacerFirstColumnStyle}></td>
                    {annualPeriods.map((_, idx) => (
                      <td key={`annual-${rowIdx}-${idx}`} style={spacerCellStyle}>
                      </td>
                    ))}
                    <td style={{ ...spacerCellStyle, ...ttmCellStyle }}></td>
                    {quarterlyPeriods.map((_, idx) => (
                      <td key={`quarter-${rowIdx}-${idx}`} style={spacerCellStyle}>
                      </td>
                    ))}
                  </>
                ) : (
                  <>
                    <td style={row.isBold ? { ...firstColumnStyle, fontWeight: 700 } : firstColumnStyle}>
                      {row.label}
                    </td>
                    {annualPeriods.map((data, idx) => {
                      const text = row.getValue(data);
                      const base = row.isBold ? { ...cellStyle, fontWeight: 700 } : cellStyle;
                      return (
                        <td key={`annual-${rowIdx}-${idx}`} style={withNegativeColor(base, text)}>
                          {text}
                        </td>
                      );
                    })}
                    {(() => {
                      const text = ttmData ? row.getValue(ttmData) : '-';
                      const base = row.isBold ? { ...ttmCellStyle, fontWeight: 700 } : ttmCellStyle;
                      return <td style={withNegativeColor(base, text)}>{text}</td>;
                    })()}
                    {quarterlyPeriods.map((data, idx) => {
                      const text = row.getValue(data);
                      const base = row.isBold ? { ...cellStyle, fontWeight: 700 } : cellStyle;
                      return (
                        <td key={`quarter-${rowIdx}-${idx}`} style={withNegativeColor(base, text)}>
                          {text}
                        </td>
                      );
                    })}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DataSourceAttribution isDarkMode={isDarkMode} />

      <FloatingNewNoteButton
        visible={showFloatingButton}
        onClick={openNewNote}
        label="New Note"
        backgroundColor={isDarkMode ? '#374151' : undefined}
      />

      <ResearchNoteModal
        isOpen={isNoteModalOpen}
        onRequestClose={() => setIsNoteModalOpen(false)}
        isDarkMode={isDarkMode}
        noteTitle={noteTitle}
        onChangeTitle={setNoteTitle}
        noteCategory={noteCategory}
        onChangeCategory={setNoteCategory}
        noteContent={noteContent}
        onChangeContent={setNoteContent}
        categories={RESEARCH_DESK_NOTE_CATEGORIES}
        canSubmit={canAddNote}
        onSubmit={async () => {
          if (!canPersist) {
            onNotify?.('Please login to save notes.', 'error');
            return;
          }

          const title = extractTitleFromContent(noteContent);
          if (!title) return;
          const associatedWith = noteCategory.trim();
          if (!associatedWith) return;

          try {
            await adminApi.createResearchDeskNote(authToken as string, {
              userId: authUserId as number,
              title,
              associatedWith,
              stockSymbol: selectedStock,
              html: noteContent ?? '',
            });
            setIsNoteModalOpen(false);
            setNoteTitle('');
            setNoteContent('');
            setNoteCategory('Cash Flow');
            onNotify?.(`${selectedStock} • Cash Flow • Saved`, 'success');
          } catch (e: any) {
            if (isUnauthorized(e)) onUnauthorized?.();
          }
        }}
      />
    </div>
  );
};

export default ResearchCashFlowScreen;

